import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { 
  CheckCircle2,
  ChevronRight,
  Target,
  RefreshCw,
  Users,
  AlertCircle
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

/**
 * Action buttons for lesson completion, assessment, and navigation
 * Now includes Phase 4 simulation requirements
 */
export const LessonActions = ({ 
  topic,
  allSectionsCompleted,
  isCurrentCompleted,
  mockTestPassed,  // True only when actual assessment passed (not just typing test)
  mockTestScore,
  hasTypingTest,
  typingTestPassed,  // Specifically for typing test status
  simulationRequirements, // Phase 4 simulation requirements
  nextTopic,
  isLastInPhase,
  onCompleteSection,
  onFinishLesson,
  onStartSimulations // Callback to scroll/show simulation section
}) => {
  const navigate = useNavigate();

  // Check simulation requirements for Phase 4
  const hasSimulations = simulationRequirements?.has_simulations;
  const simulationsComplete = simulationRequirements?.all_complete;
  const simulationsCompletedCount = simulationRequirements?.completed_count || 0;
  const simulationsTotalRequired = simulationRequirements?.total_required || 5;
  const nextSimulation = simulationRequirements?.next_simulation;
  const difficultyLevel = simulationRequirements?.difficulty_level || 'easy';

  const navigateToNext = async () => {
    // First ensure the current topic is properly marked as complete
    // This calls the completion API which handles the actual navigation
    try {
      await onFinishLesson();
    } catch (err) {
      console.error('Failed to complete current lesson:', err);
    }
  };

  const handleStartSimulation = () => {
    if (nextSimulation) {
      navigate(`/recruiter-simulation?topic=${topic.id}&sim=${nextSimulation.id}&type=${nextSimulation.scenario_type}&difficulty=${nextSimulation.difficulty}`);
    } else if (onStartSimulations) {
      onStartSimulations();
    }
  };

  // Determine what action to show based on current state
  const getNextAction = () => {
    // If simulations required and not complete, must do simulations first
    if (hasSimulations && !simulationsComplete) {
      return 'simulations';
    }
    // If typing test required and not passed
    if (hasTypingTest && !typingTestPassed) {
      return 'typing_test';
    }
    // If assessment required and not passed
    if (topic?.mock_test_id && !mockTestPassed) {
      return 'assessment';
    }
    // Everything complete
    return 'complete';
  };

  const nextAction = getNextAction();

  return (
    <div className="flex justify-center gap-4 flex-wrap mt-6">
      {/* Mark Complete button - only show if current section not completed */}
      {!isCurrentCompleted && (
        <Button onClick={onCompleteSection} variant="outline" data-testid="mark-complete-btn">
          <CheckCircle2 className="mr-2 h-4 w-4" />
          Mark Complete
        </Button>
      )}
      
      {/* Show assessment status/buttons when all sections are complete */}
      {allSectionsCompleted && (
        <>
          {mockTestPassed ? (
            // Assessment passed - show completion status and next actions
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">
                  Assessment Passed
                  {mockTestScore && ` (${Math.round(mockTestScore)}%)`}
                </span>
              </div>
              <Button 
                onClick={onFinishLesson}
                variant="outline"
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                data-testid="retake-btn"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retake
              </Button>
              
              {isLastInPhase ? (
                <Button 
                  onClick={navigateToNext}
                  className="bg-gradient-to-r from-amber-500 to-orange-500"
                  data-testid="phase-complete-btn"
                >
                  <Target className="mr-2 h-4 w-4" />
                  Phase Complete - Go to Simulations
                </Button>
              ) : nextTopic ? (
                <Button 
                  onClick={navigateToNext}
                  className="bg-gradient-to-r from-emerald-600 to-green-600"
                  data-testid="next-lesson-btn"
                >
                  <ChevronRight className="mr-2 h-4 w-4" />
                  Next Lesson: {nextTopic.code}
                </Button>
              ) : (
                <Button 
                  onClick={() => navigate('/rpat')}
                  className="bg-gradient-to-r from-emerald-600 to-green-600"
                >
                  <Target className="mr-2 h-4 w-4" />
                  Back to R-PAT
                </Button>
              )}
            </div>
          ) : nextAction === 'simulations' ? (
            // Simulations required but not complete
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                difficultyLevel === 'easy' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-orange-100 text-orange-700'
              }`}>
                <Users className="h-5 w-5" />
                <span className="font-medium">
                  {difficultyLevel === 'easy' ? 'Easy' : 'Medium'}: {simulationsCompletedCount}/{simulationsTotalRequired} Complete
                </span>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={handleStartSimulation}
                      className={`${
                        difficultyLevel === 'easy'
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                          : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
                      }`}
                      data-testid="complete-simulation-btn"
                    >
                      <Users className="mr-2 h-4 w-4" />
                      {simulationsCompletedCount === 0 
                        ? `Start ${difficultyLevel === 'easy' ? 'Easy' : 'Medium'} Simulations` 
                        : `${simulationsTotalRequired - simulationsCompletedCount} More to Go!`}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Complete all {simulationsTotalRequired} simulations on {difficultyLevel === 'easy' ? 'Easy' : 'Medium'} difficulty with a score of 80+ to proceed.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          ) : simulationsComplete && hasSimulations && !typingTestPassed && hasTypingTest ? (
            // Simulations done, typing test required
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Simulations Complete!</span>
              </div>
              <Button 
                onClick={onFinishLesson}
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                data-testid="take-typing-test-btn"
              >
                <Target className="mr-2 h-4 w-4" />
                Take Typing Test
              </Button>
            </div>
          ) : simulationsComplete && hasSimulations ? (
            // Simulations done, ready for assessment
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Simulations Complete!</span>
              </div>
              <Button 
                onClick={onFinishLesson}
                className="bg-gradient-to-r from-emerald-600 to-green-600"
                data-testid="take-assessment-btn"
              >
                <Target className="mr-2 h-4 w-4" />
                Take Assessment to Complete
              </Button>
            </div>
          ) : typingTestPassed && hasTypingTest ? (
            // Typing test passed but assessment not yet taken
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">
                  Typing Test Passed
                  {mockTestScore && ` (${Math.round(mockTestScore)}%)`}
                </span>
              </div>
              <Button 
                onClick={onFinishLesson}
                className="bg-gradient-to-r from-emerald-600 to-green-600"
                data-testid="take-assessment-btn"
              >
                <Target className="mr-2 h-4 w-4" />
                Take Assessment to Complete
              </Button>
            </div>
          ) : (
            <Button 
              onClick={onFinishLesson}
              className="bg-gradient-to-r from-emerald-600 to-green-600"
              data-testid="take-assessment-btn"
            >
              <Target className="mr-2 h-4 w-4" />
              {hasTypingTest && !typingTestPassed 
                ? 'Take Typing Test' 
                : topic?.mock_test_id 
                  ? 'Take Assessment'
                  : 'Complete Lesson'}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default LessonActions;
